import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  EmailOutlined,
  LockOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import AuthLayout from "../components/AuthLayout";
import PwaInstallPrompt from "../components/PwaInstallPrompt";
import { login } from "../services/auth";
import { useAuth } from "../context/AuthContext";
import { BRAND, inputSx, primaryButtonSx } from "../styles/authTheme";

const LoginPage = () => {
  const navigate = useNavigate();
  const { markAuthenticated } = useAuth();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      await login({ identifier: identifier.trim(), password });
      markAuthenticated();
      toast.success("Logged in — kiosk ready");
      navigate("/punch", { replace: true });
    } catch (error) {
      const message =
        error.message ||
        error.response?.data?.non_field_errors?.[0] ||
        error.response?.data?.detail ||
        (typeof error.response?.data === "object"
          ? Object.values(error.response.data).flat().join(" ")
          : null) ||
        "Login failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Employer sign-in to activate attendance kiosk"
    >
      <form onSubmit={handleSubmit}>
        <TextField
          label="Email or Mobile Number"
          type="text"
          fullWidth
          required
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          autoComplete="username"
          variant="outlined"
          sx={{ ...inputSx, mb: 2.5 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailOutlined />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Password"
          type={showPassword ? "text" : "password"}
          fullWidth
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          variant="outlined"
          sx={{ ...inputSx, mb: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlined />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((v) => !v)}
                  edge="end"
                  aria-label="toggle password visibility"
                  sx={{ color: BRAND.muted }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disableElevation
          disabled={loading}
          sx={primaryButtonSx}
        >
          {loading ? "Signing in…" : "Sign in"}
        </Button>

        <PwaInstallPrompt />
      </form>
    </AuthLayout>
  );
};

export default LoginPage;
